import { useTranslation } from 'react-i18next';

import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
} from '@/common/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/common/components/ui/select';

export const PAGE_SIZES = [10, 25, 50, 100];

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const PaginationControls = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: PaginationControlsProps) => {
  const { t } = useTranslation();
  const displayPage = totalPages === 0 ? 0 : currentPage + 1;

  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage >= totalPages - 1;
  const canGoToPreviousPage = currentPage > 0;
  const canGoToNextPage = currentPage < totalPages - 1;

  return (
    <div className="flex items-center justify-between w-full text-muted-foreground">
      <div className="flex items-center gap-4 w-full">
        <div className="flex gap-2 items-center">
          <p className="text-xs">{t('documents', { count: totalItems })}</p>
          <Select
            name="rows-per-page"
            onValueChange={(value) => onPageSizeChange(Number(value))}
            value={pageSize.toString()}
          >
            <SelectTrigger className="min-w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs">{t('rowsPerPage', { count: totalItems })}</p>
      </div>

      <Pagination className="justify-end text-xs">
        <PaginationContent className="gap-0">
          <PaginationItem>
            <PaginationFirst
              href="#"
              className={
                isFirstPage
                  ? 'border pointer-events-none opacity-50'
                  : 'border hover:bg-blue-50 cursor-pointer'
              }
              onClick={() => canGoToPreviousPage && onPageChange(0)}
            />
          </PaginationItem>
          <div className="w-2" />
          <PaginationItem>
            <PaginationPrevious
              href="#"
              className={
                isFirstPage
                  ? 'border pointer-events-none opacity-50'
                  : 'border hover:bg-blue-50 cursor-pointer'
              }
              onClick={() =>
                canGoToPreviousPage && onPageChange(currentPage - 1)
              }
            />
          </PaginationItem>
          <div className="w-4" />
          <p className="font-normal">
            Page {displayPage} {t('of')} {totalPages}
          </p>
          <div className="w-4" />
          <PaginationItem>
            <PaginationNext
              href="#"
              className={
                isLastPage
                  ? 'border pointer-events-none opacity-50'
                  : 'border hover:bg-blue-50 cursor-pointer'
              }
              onClick={() => canGoToNextPage && onPageChange(currentPage + 1)}
            />
          </PaginationItem>
          <div className="w-2" />
          <PaginationItem>
            <PaginationLast
              href="#"
              className={
                isLastPage
                  ? 'border pointer-events-none opacity-50'
                  : 'border hover:bg-blue-50 cursor-pointer'
              }
              onClick={() => canGoToNextPage && onPageChange(totalPages - 1)}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};
